import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CircularProgress } from '@mui/material';

import { ReactComponent as CashWalletCloudUploadIcon } from '@/img/icons/cash-wallet-cloud-upload-icon.svg';
import { ReactComponent as CashWalletFileIcon } from '@/img/icons/cash-wallet-file-icon.svg';
import { ReactComponent as CashWalletDeleteIcon } from '@/img/icons/cash-wallet-delete-icon.svg';

import CashWalletConstants from './CashWalletConstants';

const { INVOICE_ACCEPT, INVOICE_EXTENSION, INVOICE_MAX_SIZE_BYTES, PDF_MAGIC } = CashWalletConstants;

const MAGIC_BYTE_LENGTH = PDF_MAGIC.length;
const SPINNER_SIZE = 18;
const DRAG_TYPE_FILES = 'Files';

function isFileDrag(event) {
    const types = event.dataTransfer && event.dataTransfer.types;
    return Boolean(types) && Array.from(types).includes(DRAG_TYPE_FILES);
}

function hasPdfExtension(name) {
    return String(name || '')
        .toLowerCase()
        .endsWith(INVOICE_EXTENSION);
}

function isPdfContentType(type) {
    return String(type || '')
        .toLowerCase()
        .split(';')[0]
        .trim() === INVOICE_ACCEPT;
}

function readPdfMagic(file) {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
            const bytes = new Uint8Array(reader.result || []);
            let header = '';
            for (let i = 0; i < bytes.length; i += 1) {
                header += String.fromCharCode(bytes[i]);
            }
            resolve(header === PDF_MAGIC);
        };
        reader.onerror = () => resolve(false);
        reader.readAsArrayBuffer(file.slice(0, MAGIC_BYTE_LENGTH));
    });
}

async function validateInvoice(file) {
    if (!hasPdfExtension(file.name) || !isPdfContentType(file.type)) {
        return false;
    }
    if (file.size > INVOICE_MAX_SIZE_BYTES) {
        return false;
    }
    return readPdfMagic(file);
}

const FileRowSpinner = () => (
    <CircularProgress className="cash-wallet-file-chip-spinner" size={SPINNER_SIZE} thickness={5} />
);

// `uploading`: the attached file is in flight with the submit request, so the zone is locked. `failed`: that
// upload failed — the reason is reported by the page, so the row only carries the failure styling.
// The zone stays mounted with the single attached invoice listed below it; a new pick replaces it.
const InvoiceDropzone = ({ file, error, uploading, failed, onSelect }) => {
    const { t } = useTranslation('CashWallet');
    const inputRef = useRef(null);
    const mountedRef = useRef(true);
    const pickSeqRef = useRef(0);
    const dragDepthRef = useRef(0);
    const [dragActive, setDragActive] = useState(false);
    const [pendingName, setPendingName] = useState(null);
    const uploadingRef = useRef(uploading);
    uploadingRef.current = uploading;

    useEffect(
        () => () => {
            mountedRef.current = false;
        },
        []
    );

    const handleFiles = async (fileList) => {
        const picked = fileList && fileList[0];
        if (!picked || uploadingRef.current) {
            return;
        }
        pickSeqRef.current += 1;
        const pickSeq = pickSeqRef.current;
        setPendingName(picked.name);
        const valid = await validateInvoice(picked);
        // A newer pick supersedes this one, so its result must not land.
        if (!mountedRef.current || pickSeq !== pickSeqRef.current) {
            return;
        }
        setPendingName(null);
        // The submit started while this pick was being checked; the file in flight must not change.
        if (uploadingRef.current) {
            return;
        }
        if (valid) {
            onSelect(picked, null);
        } else {
            onSelect(null, t('Drawer.InvoiceInvalid'));
        }
    };

    const onInputChange = (event) => {
        handleFiles(event.target.files);
        event.target.value = '';
    };

    const resetDrag = () => {
        dragDepthRef.current = 0;
        setDragActive(false);
    };

    const onDrop = (event) => {
        event.preventDefault();
        resetDrag();
        if (uploading) {
            return;
        }
        handleFiles(event.dataTransfer.files);
    };

    // While locked the drag is still cancelled — otherwise the browser would open the dropped file —
    // but with a `none` drop effect so no drop happens.
    const refuseDrag = (event) => {
        event.preventDefault();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'none';
        }
    };

    const onDragEnter = (event) => {
        if (!isFileDrag(event)) {
            return;
        }
        if (uploading) {
            refuseDrag(event);
            return;
        }
        event.preventDefault();
        dragDepthRef.current += 1;
        setDragActive(true);
    };

    const onDragOver = (event) => {
        if (!isFileDrag(event)) {
            return;
        }
        if (uploading) {
            refuseDrag(event);
            return;
        }
        event.preventDefault();
        setDragActive(true);
    };

    // Entering a child fires dragenter on it before dragleave on the parent, so a depth counter tracks
    // whether the pointer is still inside the zone. `relatedTarget` is null in Safari, so it is only
    // trusted when it positively points outside the zone.
    const onDragLeave = (event) => {
        if (!isFileDrag(event)) {
            return;
        }
        event.preventDefault();
        const { relatedTarget } = event;
        if (relatedTarget && !event.currentTarget.contains(relatedTarget)) {
            resetDrag();
            return;
        }
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) {
            setDragActive(false);
        }
    };

    const openPicker = () => {
        if (!uploading && inputRef.current) {
            inputRef.current.click();
        }
    };

    const onKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openPicker();
        }
    };

    const chipFailed = !uploading && (failed || Boolean(error));
    const active = dragActive && !uploading;

    let fileRow = null;
    if (pendingName) {
        fileRow = (
            <div className="cash-wallet-file-chip cash-wallet-file-chip-pending">
                <FileRowSpinner />
                <span className="cash-wallet-file-chip-name">{pendingName}</span>
            </div>
        );
    } else if (file) {
        fileRow = (
            <div className={`cash-wallet-file-chip${chipFailed ? ' cash-wallet-file-chip-failed' : ''}`}>
                {uploading ? <FileRowSpinner /> : <CashWalletFileIcon className="cash-wallet-file-chip-icon" />}
                <span className="cash-wallet-file-chip-name">{file.name}</span>
                {!uploading && (
                    <button
                        type="button"
                        className="cash-wallet-file-chip-remove"
                        aria-label="remove"
                        onClick={() => onSelect(null, null)}
                    >
                        <CashWalletDeleteIcon className="cash-wallet-file-chip-remove-icon" />
                    </button>
                )}
            </div>
        );
    }
    const errorRow = !pendingName && error ? <div className="cash-wallet-dropzone-error">{error}</div> : null;

    return (
        <div className="cash-wallet-dropzone-wrap">
            <div
                className={`cash-wallet-dropzone${active ? ' cash-wallet-dropzone-active' : ''}${
                    uploading ? ' cash-wallet-dropzone-disabled' : ''
                }`}
                onDrop={onDrop}
                onDragEnter={onDragEnter}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onClick={openPicker}
                onKeyDown={onKeyDown}
                role="button"
                tabIndex={0}
                aria-label={t('Drawer.UploadTitle')}
                aria-disabled={uploading ? 'true' : undefined}
            >
                <CashWalletCloudUploadIcon className="cash-wallet-dropzone-icon" />
                {active ? (
                    <div className="cash-wallet-dropzone-hint">{t('Drawer.DropzoneDragActive')}</div>
                ) : (
                    <React.Fragment>
                        <div className="cash-wallet-dropzone-hint">{t('Drawer.DropzoneHint')}</div>
                        <Button
                            className="cash-wallet-dropzone-button"
                            variant="outlined"
                            color="primary"
                            disabled={Boolean(uploading)}
                            sx={{ textTransform: 'none' }}
                            onClick={(event) => {
                                event.stopPropagation();
                                openPicker();
                            }}
                        >
                            {t('Drawer.UploadButton')}
                        </Button>
                    </React.Fragment>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    accept={INVOICE_ACCEPT}
                    hidden
                    disabled={Boolean(uploading)}
                    onChange={onInputChange}
                />
            </div>
            {(fileRow || errorRow) && (
                <div className="cash-wallet-dropzone-files">
                    {fileRow}
                    {errorRow}
                </div>
            )}
        </div>
    );
};

export default InvoiceDropzone;
